import { useSyncExternalStore } from 'react'

export type BuyerCommerceContext = {
  entityId: number
  mode: 'domestic' | 'international'
  international: boolean
  countryId: number
  countryName: string | null
  countryCode: number
  locale: 'pt-BR' | 'es' | 'en'
  baseCurrency: 'BRL'
  displayCurrency: 'BRL' | 'USD'
  exchangeRate: number | null
  exchangeRateDate: string | null
  exchangeRateSource: 'taxacotacao' | 'moeda' | null
  incoterm: 'EXW' | null
  freightIncluded: boolean
  paymentTerms: 'current' | 'prepaid'
}

export const DOMESTIC_COMMERCE_CONTEXT: BuyerCommerceContext = Object.freeze({
  entityId: 0,
  mode: 'domestic',
  international: false,
  countryId: 0,
  countryName: 'Brasil',
  countryCode: 1058,
  locale: 'pt-BR',
  baseCurrency: 'BRL',
  displayCurrency: 'BRL',
  exchangeRate: null,
  exchangeRateDate: null,
  exchangeRateSource: null,
  incoterm: null,
  freightIncluded: true,
  paymentTerms: 'current',
})

const listeners = new Set<() => void>()
let currentContext: BuyerCommerceContext = DOMESTIC_COMMERCE_CONTEXT

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
)

const toNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const normalizeLocale = (value: unknown): BuyerCommerceContext['locale'] => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (normalized.startsWith('es')) return 'es'
  if (normalized.startsWith('en')) return 'en'
  return 'pt-BR'
}

const BRAZIL_TIME_ZONES = new Set([
  'America/Araguaina',
  'America/Bahia',
  'America/Belem',
  'America/Boa_Vista',
  'America/Campo_Grande',
  'America/Cuiaba',
  'America/Eirunepe',
  'America/Fortaleza',
  'America/Maceio',
  'America/Manaus',
  'America/Noronha',
  'America/Porto_Velho',
  'America/Recife',
  'America/Rio_Branco',
  'America/Santarem',
  'America/Sao_Paulo',
])

export const resolveGuestStorefrontLocale = (
  visitorCountryCode: string | null | undefined,
  browserLanguage?: string | null,
  timeZone?: string | null,
): BuyerCommerceContext['locale'] => {
  const normalizedCountryCode = String(visitorCountryCode ?? '').trim().toUpperCase()
  if (normalizedCountryCode) {
    return normalizedCountryCode === 'BR' ? 'pt-BR' : 'en'
  }

  if (BRAZIL_TIME_ZONES.has(String(timeZone ?? '').trim())) return 'pt-BR'

  const normalizedBrowserLanguage = String(browserLanguage ?? '').trim().toLowerCase()
  if (!normalizedBrowserLanguage || normalizedBrowserLanguage.startsWith('pt')) return 'pt-BR'
  return 'en'
}

export const normalizeBuyerCommerceContext = (value: unknown): BuyerCommerceContext => {
  const record = asRecord(value)
  const entityId = Math.max(0, Math.trunc(toNumber(record?.entity_id ?? record?.entityId)))
  const international = Number(record?.international ?? 0) === 1
    || String(record?.mode ?? '').trim().toLowerCase() === 'international'
  if (!international) {
    return entityId > 0
      ? { ...DOMESTIC_COMMERCE_CONTEXT, entityId }
      : DOMESTIC_COMMERCE_CONTEXT
  }

  const exchangeRate = toNumber(record?.exchange_rate ?? record?.exchangeRate)
  const exchangeRateSource = String(
    record?.exchange_rate_source ?? record?.exchangeRateSource ?? ''
  ).trim().toLowerCase()

  return {
    entityId,
    mode: 'international',
    international: true,
    countryId: Math.max(0, Math.trunc(toNumber(record?.country_id ?? record?.countryId))),
    countryName: String(record?.country_name ?? record?.countryName ?? '').trim() || null,
    countryCode: Math.max(0, Math.trunc(toNumber(record?.country_code ?? record?.countryCode))),
    locale: normalizeLocale(record?.locale),
    baseCurrency: 'BRL',
    displayCurrency: 'USD',
    exchangeRate: exchangeRate > 0 ? exchangeRate : null,
    exchangeRateDate: String(record?.exchange_rate_date ?? record?.exchangeRateDate ?? '').trim() || null,
    exchangeRateSource: exchangeRateSource === 'taxacotacao'
      ? 'taxacotacao'
      : exchangeRateSource === 'moeda'
        ? 'moeda'
        : null,
    incoterm: 'EXW',
    freightIncluded: false,
    paymentTerms: 'prepaid',
  }
}

export const resolveBuyerSessionCommerceContext = (
  context: BuyerCommerceContext | null | undefined,
  entidadeId: number | null | undefined,
) => {
  const resolvedContext = context ?? DOMESTIC_COMMERCE_CONTEXT
  if (!resolvedContext.international) return resolvedContext

  const resolvedEntidadeId = Math.max(0, Math.trunc(Number(entidadeId) || 0))
  return resolvedEntidadeId > 0 && resolvedContext.entityId === resolvedEntidadeId
    ? resolvedContext
    : DOMESTIC_COMMERCE_CONTEXT
}

export const getBuyerCommerceContext = () => currentContext

export const setBuyerCommerceContext = (value: BuyerCommerceContext | null | undefined) => {
  const nextContext = value ?? DOMESTIC_COMMERCE_CONTEXT
  if (JSON.stringify(nextContext) === JSON.stringify(currentContext)) return
  currentContext = nextContext
  listeners.forEach((listener) => listener())
}

export const resetBuyerCommerceContext = () => {
  setBuyerCommerceContext(DOMESTIC_COMMERCE_CONTEXT)
}

export const useBuyerCommerceContext = () => useSyncExternalStore(
  (listener) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  getBuyerCommerceContext,
  () => DOMESTIC_COMMERCE_CONTEXT,
)

export const convertCommerceAmount = (
  value: number,
  context: BuyerCommerceContext = currentContext,
) => {
  const normalized = Number.isFinite(value) ? value : 0
  if (!context.international) return normalized
  if (!context.exchangeRate || context.exchangeRate <= 0) return null
  return normalized / context.exchangeRate
}

export const formatCommerceCurrency = (
  value: number,
  options?: {
    minimumFractionDigits?: number
    maximumFractionDigits?: number
    context?: BuyerCommerceContext
  },
) => {
  const context = options?.context ?? currentContext
  const converted = convertCommerceAmount(value, context)
  if (converted === null) return 'USD —'

  const fractionDigits = {
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }

  if (context.international) {
    return `USD ${new Intl.NumberFormat(context.locale, fractionDigits).format(converted)}`
  }

  return new Intl.NumberFormat(context.locale, {
    style: 'currency',
    currency: context.displayCurrency,
    currencyDisplay: 'symbol',
    ...fractionDigits,
  }).format(converted)
}
